import { useCallback, useMemo, useEffect } from 'react'
import cn from 'classnames'
import { useParams } from 'react-router-dom'
import Popover from 'antd/lib/popover'
import Tooltip from 'antd/lib/tooltip'
import { useAtom } from 'jotai'
import * as atoms from '../../atoms'
import * as api from '../../../../../services/api'
import ProjectEntities from '../ProjectsEntities'
import { useTheme } from '../../../ThemeProvider/ThemeProvider'
import styles from './ProjectsList.module.scss'
import './overridePopover.scss'
import MaterialIcon from '../../../MaterialIcon'
import { show } from '../../../Modal'
import { removeHtmlTags } from '../../../../../common/editor-utils'
import ThumbImage from '../../../../../modules/Projects/components/FeatureItems/Card/ThumbImage'
import { THUMB_WIDTH } from '../../../../../modules/Projects/components/FeatureItems/Card/thumbnail-url'

const createInitials = (name) => {
  // Split the name by spaces or other common delimiters (like multiple spaces or tabs)
  const words = name.trim().split(/\s+/)

  // If there's only one word, return the first letter of that word
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase()
  }

  // Otherwise, return the first letter of the first and last words
  const firstInitial = words[0].charAt(0).toUpperCase()
  const lastInitial = words[words.length - 1].charAt(0).toUpperCase()

  return firstInitial + lastInitial
}

const ProjectItem = ({ project, selectedProject, handleProjectSelection, active }) => {
  const { themeMode } = useTheme()
  return (
    <div
      key={project.uuid}
      className={cn(
        styles.projectItem,
        styles[themeMode],
        { [styles.withCover]: project.cover_photo },
        { [styles.selected]: project.uuid === selectedProject?.uuid },
        { [styles.active]: active }
      )}
    >
      <Tooltip placement='right' title={project.title}>
        <div
          className={styles.projectItemInner}
          onClick={() => handleProjectSelection(project)}
        >
          {project.cover_photo && (
            <div className={styles.coverWrapper}>
              {active && (<div className={styles.overlay} />)}
              <ThumbImage key={project.cover_photo} src={project.cover_photo} width={THUMB_WIDTH.tile} alt={removeHtmlTags(project.title)} />
            </div>
          )}
          {!project.cover_photo && (
            <div className={cn(styles.initialsWrapper, styles[themeMode])}>
              <span>{createInitials(removeHtmlTags(project.title))}</span>
            </div>
          )}
        </div>
      </Tooltip>
      {project.uuid === selectedProject?.uuid && (
        <Popover rootClassName='project-entity-popover' placement='right' open content={<ProjectEntities />} />
      )}
    </div>
  )
}

const ProjectsList = () => {
  const { themeMode } = useTheme()
  const [projects] = useAtom(atoms.projects)
  const [active, setActive] = useAtom(atoms.activeProject)
  const { slug, scriptUuid: scriptPathUuid, projectSlug: projectUuid } = useParams()
  const [projectsIsLoading] = useAtom(atoms.projectsIsLoading)
  const [selectedProject, setSelectedProject] = useAtom(atoms.selectedProject)

  const scriptUuid = useMemo(() => {
    return slug || scriptPathUuid
  }, [slug, scriptPathUuid])

  const handleProjectSelection = useCallback((project) => {
    const selection = project.uuid === selectedProject?.uuid ? null : project
    setSelectedProject(selection)
  }, [selectedProject])

  const activeProject = useMemo(() => {
    return active ? projects.find(p => p.uuid === (active?.project_uuid || active?.uuid || active)) : null
  }, [projects, active])

  const projectList = useMemo(() => {
    if (active) {
      return projects.filter(p => p.uuid !== (active?.project_uuid || active?.uuid || active))
    }
    return projects
  }, [projects, active])

  const checkBelongsToProject = useCallback(async () => {
    try {
      const isBelong = await api.project.getSingleProject(projectUuid)
      if (isBelong) {
        setActive(isBelong)
      }
    } catch (e) {}
  }, [scriptUuid, projectUuid])

  useEffect(() => {
    if (scriptUuid) {
      checkBelongsToProject()
    } else if (active) {
      setActive(null)
    }
  }, [])

  return projectsIsLoading
    ? (<div className={cn(styles.projectsListWrapper, styles[themeMode])} />)
    : (
      <div className={cn(styles.projectsListWrapper, styles[themeMode])}>
        {!projects.length && (
          <div
            className={cn(
              styles.projectItem,
              styles[themeMode],
              { [styles.active]: true }
            )}
          >
            <Tooltip placement='right' title='Start New Project'>
              <div
                className={styles.projectItemInner}
                onClick={() => show('createProject')}
              >
                <MaterialIcon name='add_circle_outline' />
              </div>
            </Tooltip>
          </div>
        )}
        {activeProject && (
          <ProjectItem
            key={activeProject.uuid}
            project={activeProject}
            selectedProject={selectedProject}
            active
            handleProjectSelection={handleProjectSelection}
          />
        )}
        {projectList.map((project) => (
          <ProjectItem
            key={project.uuid}
            project={project}
            selectedProject={selectedProject}
            handleProjectSelection={handleProjectSelection}
          />
        ))}
      </div>
      )
}

export default ProjectsList
